import { useRef } from "react"

import { Header } from "~/components/landing/Header"
import { Footer } from "~/components/landing/Footer"
import { PageHero } from "~/components/landing/PageHero"
import { ForestBackdrop } from "~/components/landing/ForestBackdrops"
import { relatedPosts, type Post } from "~/data/posts"
import { FeedPostCard } from "./PostCard"
import { TableOfContents } from "./TableOfContents"

// The frame every blog post renders inside: pinned Header, a forest hero band
// (the topo data-texture, or the post's own hero image), a two-column reading
// area (sticky contents rail + article), then a "keep reading" grid. Post
// routes supply only the body — a sequence of the helpers in
// components/blog/prose.tsx, or <PlaceholderPost>.
//
// The launch post drops a <DocsBanner /> mid-body, so children sit outside a
// single fixed-width <article> and each post wraps its prose in <BlogProse>.

export function BlogProse({ children }: { children: React.ReactNode }) {
  return <div className="u-container max-w-3xl">{children}</div>
}

function KeepReading({ slug }: { slug: string }) {
  const posts = relatedPosts(slug, 3)
  if (posts.length === 0) return null

  return (
    <div className="u-container max-w-[1600px] border-t border-line pt-14">
      <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post) => (
          <FeedPostCard key={post.slug} post={post} />
        ))}
      </div>
    </div>
  )
}

// A post's `heroImage` behind its hero, so the top of the post picks up the
// same graphic language as its /blog card. Use text-free art (the cover's
// artwork layer, not a cover with a baked-in logo or headline, which would
// collide with the title). From xl the art starts 200px inside the right edge
// of the 768px text column and runs to the viewport edge, so its centred
// subject (the car, the phone) lands beside the text, dissolving into the
// topo texture on the left. Below xl the text fills the band, so the art goes
// full-bleed and dim.
function CoverBackdrop({ src }: { src: string }) {
  return (
    <>
      <ForestBackdrop />
      <img
        src={src}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover opacity-15 xl:left-[calc(50%+184px)] xl:w-[calc(50%-184px)] xl:opacity-90 xl:[mask-image:linear-gradient(to_right,transparent,black_35%)]"
      />
      {/* keeps the title and standfirst at full contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-forest/80 via-forest/50 to-transparent xl:via-forest/30" />
    </>
  )
}

export function BlogLayout({
  post,
  children,
}: {
  post: Post
  children: React.ReactNode
}) {
  const { title, summary, dek, publishedDisplay, heroImage } = post
  const bodyRef = useRef<HTMLDivElement>(null)

  return (
    <div className="bg-paper">
      <Header />

      <PageHero
        title={title}
        eyebrow={publishedDisplay}
        backTo="/blog"
        backLabel="Blog"
        maxWidth="max-w-3xl"
        backdrop={heroImage ? <CoverBackdrop src={heroImage} /> : undefined}
      >
        <p className="mt-3 max-w-xl text-[17px] leading-relaxed text-paper/70">
          {dek ?? summary}
        </p>
      </PageHero>

      <div className="py-14 sm:py-16">
        {/* Contents sits directly against the article's own box (a hairline
            border-l + pl-6), so it needs no width math — it just sits in the
            space the centered article column already leaves free. Hidden
            below xl (1280px): the column is 768px and the rail 240px, so on
            anything narrower the rail would run past the right edge of the
            viewport. The border/padding live
            on the sticky nav itself (see TableOfContents), not this
            full-height wrapper — otherwise the divider runs the entire
            length of the article instead of just the Contents list.

            This wrapper mirrors BlogProse's own box (mx-auto max-w-3xl)
            WITHOUT its px-6/sm:px-8 gutter — children already supply that
            via their own <BlogProse>, so adding u-container's padding here
            too would double it and shift the article out of line with the
            hero title above. */}
        <div className="relative mx-auto w-full max-w-3xl">
          <aside className="absolute inset-y-0 left-full hidden w-60 xl:block">
            <TableOfContents containerRef={bodyRef} />
          </aside>

          <div ref={bodyRef}>{children}</div>
        </div>
      </div>

      <div className="pb-16">
        <KeepReading slug={post.slug} />
      </div>

      <Footer />
    </div>
  )
}
